import { API_BASE } from './api.js';

export type ClientLogLevel = 'info' | 'warn' | 'error';

export interface ClientLogOptions {
  level?: ClientLogLevel;
  meetingId?: string;
}

interface QueuedEntry {
  level: ClientLogLevel;
  event: string;
  meetingId?: string;
  detail?: Record<string, unknown>;
  at: string;
}

/** The server accepts 60/min per user; one request per second is the ceiling here. */
const MIN_INTERVAL_MS = 1000;
/** A diagnostic channel must never grow into a memory leak. */
const MAX_QUEUE = 50;

const queue: QueuedEntry[] = [];
let timer: ReturnType<typeof setTimeout> | null = null;
let sending = false;

async function send(entry: QueuedEntry): Promise<void> {
  try {
    await fetch(`${API_BASE}/client-log`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(entry),
      keepalive: true,
    });
  } catch {
    /* diagnostics must never break, or delay, the upload they describe */
  }
}

function schedule(delayMs: number): void {
  if (timer !== null) return;
  timer = setTimeout(() => {
    timer = null;
    void flush();
  }, delayMs);
}

async function flush(): Promise<void> {
  if (sending) {
    schedule(MIN_INTERVAL_MS);
    return;
  }
  const entry = queue.shift();
  if (!entry) return;
  sending = true;
  try {
    await send(entry);
  } finally {
    sending = false;
    if (queue.length > 0) schedule(MIN_INTERVAL_MS);
  }
}

/**
 * Fire-and-forget diagnostics. Never throws, never awaits, and never issues
 * more than one request per second however loud the caller is.
 */
export function clientLog(
  event: string,
  detail?: Record<string, unknown>,
  options: ClientLogOptions = {},
): void {
  try {
    queue.push({
      level: options.level ?? 'info',
      event,
      ...(options.meetingId ? { meetingId: options.meetingId } : {}),
      // The build travels with every entry: not knowing which web build the
      // iPhone was running is what made the silence incident hard to trace.
      detail: { ...(detail ?? {}), build: __BUILD_ID__ },
      at: new Date().toISOString(),
    });
    // Oldest first: the tail is what explains where a stuck upload ended up.
    while (queue.length > MAX_QUEUE) queue.shift();
    schedule(0);
  } catch {
    /* never throws */
  }
}
